import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Datastore } from '@freelancer/datastore';
import type {
  TaskGroup,
  TaskGroupCollection,
} from '@freelancer/datastore/collections';
import { trackById } from '@freelancer/ui/helpers';
import { IconSize } from '@freelancer/ui/icon';
import { TabsDirection, TabsSize } from '@freelancer/ui/tabs';
import { TaskGroupStatusApi } from 'api-typings/tasklist/tasklist';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * This component lists all task-lists.
 */
@Component({
  selector: 'fl-tasklists',
  template: `
    <fl-scrollable-content class="TasklistsScrollableContent">
      <fl-tabs
        [direction]="TabsDirection.COLUMN_RIGHT"
        [truncate]="true"
        [size]="TabsSize.XSMALL"
      >
        <ng-container
          *ngIf="tasklistsToShow$ | flAsync as taskGroups"
          ngProjectAs="fl-tab-item"
        >
          <fl-tab-item
            *ngFor="let taskGroup of taskGroups; trackBy: trackById"
            flTrackingLabel="TaskGroupsCollectionClick"
            flTrackingSection="taskGroupLists"
            [flTrackingReferenceType]="'task_group_id'"
            [flTrackingReferenceId]="taskGroup.id"
            [iconName]="'ui-playlist-add-check'"
            [iconSize]="IconSize.SMALL"
            [link]="'/tasklists/' + taskGroup.id"
            [preserveBackButton]="false"
            [titleText]="taskGroup.name"
          >
          </fl-tab-item>
        </ng-container>
      </fl-tabs>
    </fl-scrollable-content>
  `,
  styleUrls: ['./tasklists.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TasklistsComponent implements OnInit {
  IconSize = IconSize;
  TabsDirection = TabsDirection;
  TabsSize = TabsSize;
  trackById = trackById;

  @Input() customLimit = 3;

  tasklistsToShow$: Observable<readonly TaskGroup[]>;

  constructor(private datastore: Datastore) {}

  ngOnInit(): void {
    const taskGroupCollection = this.datastore.collection<TaskGroupCollection>(
      'taskGroup',
      query => query.where('status', '==', TaskGroupStatusApi.OPEN),
    );

    this.tasklistsToShow$ = taskGroupCollection
      .valueChanges()
      .pipe(map(taskGroups => taskGroups.slice(0, this.customLimit)));
  }
}
